import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Send, Check, DoorOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { translations, fmt } from "@/lib/translations";
import { GemStar, LangToggle, LoadingScreen, LogoMark, detectLang, saveLang } from "@/components/brand";
import NotFound from "@/pages/NotFound";
import { useSeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const hasGoogleUrl = (url) => !!url && url !== "#" && /^https?:\/\//i.test(url);

const ease = [0.22, 1, 0.36, 1];

export const GuestFeedback = () => {
  useSeo({ noindex: true });
  const { hotelSlug, roomNumber: roomFromPath } = useParams();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [popped, setPopped] = useState(0);
  const [message, setMessage] = useState("");
  const [roomNumber, setRoomNumber] = useState(roomFromPath || "");
  const [submitted, setSubmitted] = useState(null); // null | "low" | "high"
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHotel, setIsLoadingHotel] = useState(true);
  const [lang, setLang] = useState(detectLang);
  const [hotel, setHotel] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const t = translations[lang];
  const isAutoDetected = !!roomFromPath;

  useEffect(() => {
    setRoomNumber(roomFromPath || "");
  }, [roomFromPath]);

  useEffect(() => {
    let alive = true;
    if (!hotelSlug) {
      setNotFound(true);
      setIsLoadingHotel(false);
      return undefined;
    }
    axios
      .get(`${API}/hotels/${hotelSlug}/public`)
      .then((res) => alive && setHotel(res.data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setIsLoadingHotel(false));
    return () => {
      alive = false;
    };
  }, [hotelSlug]);

  const changeLang = (code) => {
    setLang(code);
    saveLang(code);
  };

  const pickRating = (n) => {
    setRating(n);
    setPopped(n);
    setTimeout(() => setPopped(0), 450);
  };

  const postFeedback = (extra = {}) =>
    axios.post(`${API}/feedback`, {
      rating,
      message: message.trim() || null,
      room_number: roomNumber || null,
      hotel_slug: hotelSlug,
      ...extra,
    });

  const handleLowSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await postFeedback();
      setSubmitted("low");
    } catch (error) {
      toast.error(t.feedbackFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogle = async () => {
    setIsLoading(true);
    try {
      await postFeedback({ message: null });
    } catch (error) {
      /* recording the rating must never block the guest from reviewing */
    } finally {
      setIsLoading(false);
    }
    if (hasGoogleUrl(hotel.google_maps_url)) {
      window.location.href = hotel.google_maps_url;
    } else {
      setSubmitted("high");
    }
  };

  if (isLoadingHotel) return <LoadingScreen />;
  if (notFound || !hotel) return <NotFound />;

  const display = hovered || rating;
  const isHigh = rating >= 4;
  const isLow = rating >= 1 && rating <= 3;
  const googleOk = hasGoogleUrl(hotel.google_maps_url);

  return (
    <div className="scene flex flex-col" style={{ minHeight: "100dvh" }}>
      <div className="orb" style={{ width: 380, height: 380, left: "50%", top: 120, marginLeft: -190, background: "rgba(124,58,237,.35)" }} />

      <header className="wrap flex items-center justify-between pt-5">
        <div className="flex items-center gap-2 faint text-sm">
          {roomNumber && (
            <span className="chip">
              <DoorOpen size={14} /> {t.room} {roomNumber}
            </span>
          )}
        </div>
        <LangToggle lang={lang} onChange={changeLang} />
      </header>

      <main className="wrap flex-1 flex flex-col items-center justify-center py-10">
        <div className="w-full" style={{ maxWidth: 460 }}>
          {/* Hotel identity */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="flex flex-col items-center text-center mb-8"
            data-testid="hotel-logo-container"
          >
            {hotel.logo_url ? (
              <div
                className="panel grid place-items-center mb-5"
                style={{ width: 88, height: 88, borderRadius: 24, padding: 14 }}
              >
                <img
                  src={hotel.logo_url}
                  alt={hotel.name}
                  className="max-h-full max-w-full object-contain"
                  data-testid="hotel-logo-image"
                />
              </div>
            ) : (
              <div
                className="step-token mb-5"
                style={{ width: 72, height: 72, borderRadius: 22, fontSize: "2.2rem" }}
                aria-hidden="true"
              >
                {hotel.name.trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="eyebrow mb-2">{t.welcomeTo}</div>
            <div className="display" style={{ fontSize: "clamp(1.8rem, 6vw, 2.3rem)" }}>
              {hotel.name}
            </div>
          </motion.div>

          {/* Rating card */}
          <motion.section
            initial={{ opacity: 0, y: 24, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="panel panel-rim"
            style={{ padding: "clamp(1.4rem, 5vw, 2.2rem)", transformPerspective: 1200 }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {!submitted ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <h1
                    className="display text-center"
                    style={{ fontSize: "clamp(1.9rem, 7vw, 2.5rem)", marginBottom: "1.6rem" }}
                    data-testid="main-heading"
                  >
                    {t.mainHeading}
                  </h1>

                  <div
                    className="stars"
                    role="radiogroup"
                    aria-label={t.mainHeading}
                    data-testid="star-rating-container"
                    onMouseLeave={() => setHovered(0)}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={rating === n}
                        aria-label={`${n} / 5 — ${t.ratingWords[n]}`}
                        onClick={() => pickRating(n)}
                        onMouseEnter={() => setHovered(n)}
                        onFocus={() => setHovered(n)}
                        onBlur={() => setHovered(0)}
                        className={`star-btn ${n <= display ? "is-lit" : ""} ${popped && n <= popped ? "pop" : ""}`}
                        style={{ transitionDelay: `${(n - 1) * 25}ms` }}
                        data-testid={`star-${n}`}
                      >
                        <GemStar lit={n <= display} />
                      </button>
                    ))}
                  </div>

                  <p className="rating-word text-center mt-4" aria-live="polite">
                    {display ? t.ratingWords[display] : <span className="faint">{t.tapToRate}</span>}
                  </p>

                  <AnimatePresence mode="wait" initial={false}>
                    {isHigh && (
                      <motion.div
                        key="high"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.45, ease }}
                        className="overflow-hidden"
                        data-testid="high-rating-section"
                      >
                        <div className="pt-6 flex flex-col items-center text-center gap-5">
                          <hr className="divider w-full" />
                          <p className="muted" style={{ maxWidth: "36ch" }}>
                            {t.highRatingMessage}
                          </p>
                          <button
                            type="button"
                            onClick={handleGoogle}
                            disabled={isLoading}
                            className="btn btn-primary btn-block"
                            data-testid="google-maps-button"
                          >
                            <GoogleG />
                            {t.reviewOnGoogleMaps}
                            <ExternalLink size={16} />
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {isLow && (
                      <motion.div
                        key="low"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.45, ease }}
                        className="overflow-hidden"
                        data-testid="low-rating-section"
                      >
                        <div className="pt-6">
                          <hr className="divider w-full mb-5" />
                          <p className="muted text-center mb-5">{t.lowRatingMessage}</p>

                          <form onSubmit={handleLowSubmit} className="flex flex-col gap-4">
                            <textarea
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              placeholder={t.feedbackPlaceholder}
                              className="field"
                              rows={4}
                              maxLength={2000}
                              aria-label={t.feedbackPlaceholder}
                              data-testid="feedback-message-input"
                            />

                            {isAutoDetected ? (
                              <p className="field-hint flex items-center gap-2" data-testid="room-number-input">
                                <DoorOpen size={14} className="text-[var(--violet-300)]" />
                                {fmt(t.roomAutoDetected, { room: roomNumber })}
                              </p>
                            ) : (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={roomNumber}
                                onChange={(e) => setRoomNumber(e.target.value.slice(0, 12))}
                                placeholder={t.roomNumberPlaceholder}
                                aria-label={t.roomNumberPlaceholder}
                                className="field"
                                data-testid="room-number-input"
                              />
                            )}

                            <button
                              type="submit"
                              disabled={isLoading}
                              className="btn btn-primary btn-block mt-1"
                              data-testid="submit-feedback-button"
                            >
                              <Send size={17} />
                              {isLoading ? t.sending : t.sendToFrontDesk}
                            </button>
                          </form>

                          {googleOk && (
                            <p className="text-center text-sm faint mt-6">
                              {t.alsoGoogle}{" "}
                              <a
                                href={hotel.google_maps_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-4 decoration-[var(--line-strong)] hover:text-[var(--text)]"
                                data-testid="low-google-link"
                              >
                                {t.alsoGoogleLink}
                              </a>
                            </p>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease }}
                  className="flex flex-col items-center text-center py-4"
                  data-testid="submission-confirmation"
                >
                  <div
                    className="grid place-items-center mb-6"
                    style={{
                      width: 76,
                      height: 76,
                      borderRadius: 24,
                      background: "linear-gradient(160deg, #6ee7a8, #15803d)",
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,.5), inset 0 -3px 0 rgba(0,0,0,.25), 0 5px 0 #0b4d26, 0 20px 40px -12px rgba(74,222,128,.55)",
                    }}
                  >
                    <Check size={36} color="#fff" strokeWidth={3} />
                  </div>
                  <h2 className="display" style={{ fontSize: "2.2rem" }}>
                    {t.thankYou}
                  </h2>
                  <p className="muted mt-3" style={{ maxWidth: "34ch" }}>
                    {submitted === "low" ? t.confirmationMessage : t.confirmationHigh}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>
        </div>
      </main>

      <footer className="pb-7 pt-2 flex flex-col items-center gap-2">
        <a
          href="/"
          className="flex items-center gap-2 faint text-xs no-underline hover:text-[var(--text-2)] transition-colors"
          data-testid="rateguard-footer-logo"
        >
          <LogoMark size={20} />
          <span className="mono" style={{ letterSpacing: "0.14em", textTransform: "uppercase" }} data-testid="footer-text">
            {t.poweredBy}
          </span>
        </a>
        <a href={`/gizlilik?lang=${lang}`} className="faint text-xs underline underline-offset-4 hover:text-[var(--text-2)]">
          {lang === "tr" ? "Gizlilik" : lang === "sr" ? "Privatnost" : "Privacy"}
        </a>
      </footer>
    </div>
  );
};

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#fff" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" />
  </svg>
);

export default GuestFeedback;
